<template>
  <MasterDetail
    class="cart-rule-overview"
    data-test-id="cartRuleOverview"
    :detail-open="!!selectedRule"
    @table:resize="tableWidth = $event"
  >
    <template #toolbar>
      <Toolbar
        v-model="filterQuery"
        :search-props="{ possibleFilters, disableFullTextSearch: true }"
        :key="reloadFilters"
        :disabled="runningAction"
        @update-filters="updateFilter"
      >
        <template #actions>
          <v-switch
            v-model="showParentRules"
            label="Include rules of parents"
            hide-details
            data-test-id="includeParentRulesSelect"
            class="mr-2"
          />
          <v-divider inset vertical class="mx-3" />
          <v-btn
            color="primary"
            data-test-id="newCartRuleBtn"
            title="Create a new cart rule"
            :disabled="runningAction || isModeCreate"
            :fab="showMinified"
            :small="showMinified"
            @click="openRuleDetail()"
          >
            <v-icon v-if="showMinified">mdi-plus</v-icon>
            <div v-else>New Cart Rule</div>
          </v-btn>
        </template>
      </Toolbar>
    </template>

    <template #table>
      <v-data-table
        class="cart-rule-table"
        dense
        fixed-header
        multi-sort
        single-select
        item-key="id"
        :item-class="getItemClass"
        :height="tableHeight"
        :items-per-page="limit"
        :items="displayedCartRules"
        :headers="headers"
        :options.sync="options"
        :loading="runningAction"
        :server-items-length="total"
        :footer-props="{ itemsPerPageOptions: [limit], showFirstLastPage: true }"
        @click:row="openRuleDetail"
      >
        <!-- eslint-disable-next-line -->
        <template #item.validFrom="{ item }">
          {{
            item.validFrom
              ? $getLocalizedDate(item.validFrom, {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric"
                })
              : "-"
          }}
        </template>
        <!-- eslint-disable-next-line -->
        <template #item.validUntil="{ item }">
          {{
            item.validUntil
              ? $getLocalizedDate(item.validUntil, {
                  year: "numeric",
                  month: "numeric",
                  day: "numeric"
                })
              : "-"
          }}
        </template>
        <!-- eslint-disable-next-line -->
        <template #item.name="{ item }">
          {{ displayName(item.name) }}
        </template>
        <!-- eslint-disable-next-line -->
        <template #item.status="{ item }">
          <v-chip
            v-if="item.status"
            x-small
            outlined
            :color="statusColor(item.status)"
            data-test-id="cartRuleStatusChip"
          >
            {{ item.status }}
          </v-chip>
        </template>
      </v-data-table>
    </template>

    <template #detail>
      <CartRuleDetail
        v-if="selectedRule"
        data-test-id="cartRuleDetail"
        :key="selectedRule.id + reloadDetailKey"
        :rule="selectedRule"
        :action-types="actionTypes"
        :condition-types="conditionTypes"
        :disabled="detailDisabled"
        :style="{
          height: detailHeight + 'px'
        }"
        @close="closeRuleDetail"
        @reload="init(true)"
      />
    </template>
  </MasterDetail>
</template>

<script>
import MasterDetail from "../common/templates/MasterDetail";
import CartRuleDetail from "./CartRuleDetail";
import Toolbar from "../common/templates/Toolbar";
import mainOverviewMixin from "../../mixins/main-overview-mixin";

export default {
  mixins: [mainOverviewMixin],

  components: {
    MasterDetail,
    CartRuleDetail,
    Toolbar
  },

  data() {
    return {
      runningAction: false,
      rules: [],
      total: 0,
      selectedRule: null,
      createRule: false,
      actionTypes: [],
      conditionTypes: [],
      options: {
        page: 1,
        sortBy: ["priority", "name"],
        sortDesc: [true, false]
      },
      reloadDetailKey: 0,
      showParentRules: null,
      tableWidth: 0,
      filters: {},
      filterQuery: null,
      reloadFilters: 0
    };
  },

  mounted() {
    this.init();
  },

  watch: {
    options(options) {
      let query = Object.assign({}, this.$route.query);
      const sortBy = options.sortBy;
      const sortDesc = options.sortDesc;
      let sort = "";

      if (Array.isArray(sortBy)) {
        for (let i = 0; i < sortBy.length; i++) {
          const sortQuery = (sortDesc[i] ? "-" : "") + sortBy[i];
          if (!sort) sort = sortQuery;
          else sort += "," + sortQuery;
        }
      }

      if (!sort) this.$delete(query, "sort");
      else this.$set(query, "sort", sort);

      query.page = "" + (options.page ?? 1);

      this.$router.push({
        ...this.$route,
        query
      });

      if (!this.runningAction) {
        this.$nextTick(() => this.getRules());
      }
    },

    showParentRules(showParentRules) {
      let query = Object.assign({}, this.$route.query);
      this.$set(query, "parent-rules", showParentRules);
      this.$router.push({
        ...this.$route,
        query
      });
    },

    filters: {
      handler: function(filters) {
        let query = Object.assign({}, this.$route.query);
        if (Object.keys(filters).length > 0) {
          let filterQuery = this.filterQuery;
          if (this.$isPlainObject(filterQuery))
            filterQuery = JSON.stringify(filterQuery);
          query.filter = this.$urlEncode(filterQuery);
        } else {
          delete query.filter;
        }
        this.$router.replace({ ...this.$route, query });
        this.options = Object.assign({}, this.options, { page: 1 });
      },
      deep: true
    }
  },

  methods: {
    async init(updateRules) {
      try {
        this.runningAction = true;
        if (updateRules) {
          await this.getRules();
        } else {
          const routeQuery = this.$route.query;
          let sort = routeQuery.sort;
          let showParentRules = routeQuery["parent-rules"];
          let page = routeQuery.page;
          let filter = routeQuery.filter;

          if (page) {
            page = parseInt(page, 10);
            if (!Number.isNaN(page))
              this.options = Object.assign(this.options, { page });
          }

          if (filter) {
            try {
              this.filterQuery = JSON.parse(this.$urlDecode(filter));
            } catch (e) {
              this.filterQuery = null;
            }
          }

          if (sort) {
            const sortBy = [];
            const sortDesc = [];
            const sortQuery = sort.split(",");

            const addToSorted = sortString => {
              const isDesc = sortString.startsWith("-");
              const property = isDesc ? sortString.substring(1) : sortString;
              const isSortableField = this.headers.some(
                header => header.value === property && header.sortable !== false
              );
              if (isSortableField) {
                sortBy.push(property);
                sortDesc.push(isDesc);
              }
            };

            if (Array.isArray(sortQuery))
              sortQuery.forEach(part => addToSorted(part));
            else addToSorted(sortQuery);
            this.options = Object.assign(this.options, { sortBy, sortDesc });
          }

          this.showParentRules = showParentRules !== "false";

          await Promise.all([
            this.getRules(),
            this.getActionTypes(),
            this.getConditionTypes()
          ]);
        }

        if (this.$route.matched.some(({ name }) => name === "cartRuleDetail")) {
          const ruleId = this.$route?.params?.rule;
          let rule = this.rules.find(({ id }) => ruleId === id);
          if (!rule) {
            if (updateRules) {
              // Rule was just saved but may be on a different page due to sort/pagination.
              // Fetch it directly rather than closing the detail.
              rule = await this.$store.$coreApi.coreCartRuleApi.getRule(
                this.selectedDomain,
                ruleId,
                { returnErrors: true }
              );
              // On error, getRule returns the raw Response (ok=false); on success it returns parsed JSON.
              if (!rule || rule?.ok === false) {
                this.$store.dispatch(
                  "setError",
                  "Cannot find cart rule with id " + ruleId
                );
                this.closeRuleDetail();
                return;
              }
            } else {
              this.$store.dispatch(
                "setError",
                "Cannot find cart rule with id " + ruleId
              );
              this.closeRuleDetail();
              return;
            }
          }
          if (this.selectedRule?.id === ruleId) {
            this.selectedRule = rule;
          } else {
            this.openRuleDetail(rule);
          }
        } else {
          this.selectedRule = null;
        }
      } finally {
        this.reloadDetailKey++;
        this.runningAction = false;
      }
    },

    async getRules() {
      const request = this.buildSearchRequest();
      const result = await this.$store.$coreApi.coreCartRuleApi.searchRules(
        this.selectedDomain,
        request
      );
      this.rules = result?.result ?? [];
      this.total = result?.total ?? result?.count ?? 0;
    },

    buildSearchRequest() {
      const offset = ((this.options.page ?? 1) - 1) * this.limit;
      const request = {
        limit: this.limit,
        offset,
        total: true,
        filter: {},
        sort: []
      };

      for (let index in this.filters) {
        const filter = this.filters[index];
        const { property, value } = filter;

        if (property === "name") {
          request.filter.name = value;
        } else if (
          property === "activationDateFrom" ||
          property === "activationDateTo"
        ) {
          request.filter[property] = value;
        } else if (property === "priority") {
          if (!request.filter.priority) request.filter.priority = [];
          request.filter.priority.push(Number(value));
        } else if (property === "status") {
          if (!request.filter.status) request.filter.status = [];
          request.filter.status.push(value);
        }
      }

      const sortBy = this.options?.sortBy ?? [];
      const sortDesc = this.options?.sortDesc ?? [];
      for (let i = 0; i < sortBy.length; i++) {
        request.sort.push({
          field: sortBy[i],
          direction: sortDesc[i] ? "desc" : "asc"
        });
      }

      return request;
    },

    updateFilter(filters) {
      this.$nextTick(() => {
        this.filters = this.$cloneObject(filters);
      });
    },

    async getActionTypes() {
      const actionTypes = await this.$store.$coreApi.coreCartRuleApi.getActionTypes(
        this.selectedDomain
      );

      this.actionTypes = actionTypes;
    },

    async getConditionTypes() {
      const conditionTypes = await this.$store.$coreApi.coreCartRuleApi.getConditionTypes(
        this.selectedDomain
      );

      this.conditionTypes = conditionTypes;
    },

    async openRuleDetail(rule = {}) {
      if (!rule.id) {
        this.$set(rule, "id", this.$uuid.v4());
        this.$set(rule, "isNew", true);
      }

      await this.$router.push({
        name: "cartRuleDetail",
        params: {
          rule: rule.id
        },
        query: this.$route.query
      });

      this.selectedRule = rule;
    },

    async closeRuleDetail(reload) {
      await this.$router.push({
        name: "cartRules",
        query: this.$route.query
      });

      this.selectedRule = false;
      if (reload) this.init(true);
    },

    getItemClass(item) {
      let itemClass = "cart-rule-item";
      if (item.id === this.selectedRule?.id) itemClass += " selected";
      if (item.domainId !== this.selectedDomain) itemClass += " inherited";
      return itemClass;
    },

    displayName(name) {
      if (!name) return "-";
      if (typeof name === "string") return name;
      const fallback = this.$store.state.localization.fallbackLocale;
      return name[fallback] || Object.values(name)[0] || "-";
    },

    statusColor(status) {
      switch (status) {
        case "ACTIVE":
          return "green";
        case "DRAFT":
          return "grey";
        case "DEACTIVATED":
          return "orange";
        case "EXPIRED":
          return "red";
        default:
          return undefined;
      }
    }
  },

  computed: {
    selectedDomain() {
      return this.$store.state.selectedDomain;
    },

    tableHeight() {
      return this.getPageHeight() - 80 - 59;
    },

    limit() {
      return 20;
    },

    headers() {
      return [
        {
          text: "Priority",
          value: "priority",
          cellClass: "cart-rule-priority"
        },
        { text: "Name", value: "name", cellClass: "cart-rule-name" },
        { text: "Domain", value: "domainId", cellClass: "cart-rule-domain" },
        {
          text: "Valid from",
          value: "validFrom",
          cellClass: "cart-rule-valid-from"
        },
        {
          text: "Valid until",
          value: "validUntil",
          cellClass: "cart-rule-valid-until"
        },
        {
          text: "Status",
          value: "status",
          cellClass: "cart-rule-status"
        }
      ];
    },

    possibleFilters() {
      return [
        {
          text: "Name",
          property: "name",
          type: "text",
          operator: "contains"
        },
        {
          text: "Active from",
          property: "activationDateFrom",
          type: "date",
          dateOnly: true
        },
        {
          text: "Active until",
          property: "activationDateTo",
          type: "date",
          dateOnly: true
        },
        {
          text: "Priority",
          property: "priority",
          type: "number"
        },
        {
          text: "Status",
          property: "status",
          type: "list",
          options: [
            { text: "Draft", value: "DRAFT" },
            { text: "Active", value: "ACTIVE" },
            { text: "Deactivated", value: "DEACTIVATED" },
            { text: "Expired", value: "EXPIRED" }
          ]
        }
      ];
    },

    displayedCartRules() {
      const rules = this.rules ?? [];
      if (!this.showParentRules) {
        return rules.filter(({ domainId }) => domainId === this.selectedDomain);
      }
      return rules;
    },

    detailDisabled() {
      const rule = this.selectedRule;
      return !rule?.isNew && this.selectedDomain !== rule?.domainId;
    },

    showMinified() {
      return (
        this.getPageWidth() <= 900 ||
        (this.selectedRule && this.tableWidth <= 900)
      );
    },

    isModeCreate() {
      return this.selectedRule?.isNew;
    }
  }
};
</script>

<style scoped>
.cart-rule-overview .cart-rule-table::v-deep .cart-rule-item.inherited {
  opacity: 0.5;
}
</style>
